/**
 * DriverMarketplacePanel — Stripe onboarding + annual earnings CSV for drivers.
 * Renders below the DriverProfile card.
 */
import React, { useEffect, useState } from 'react';
import { ShieldAlert, Download, ExternalLink, Loader2 } from 'lucide-react';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { useApp } from '../context/AppContext';
import { toast } from 'sonner';

const CURRENT_YEAR = new Date().getFullYear();

const DriverMarketplacePanel = () => {
  const { format } = useApp();
  const [status, setStatus] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [summary, setSummary] = useState(null);
  const [year, setYear] = useState(CURRENT_YEAR);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const s = await api.get('/driver/stripe/status').then((r) => r.data);
      setStatus(s);
    } catch (e) { /* Connect may not be enabled — still show the summary */ }
    setLoading(true);
    try {
      const r = await api.get('/driver/tax/summary', { params: { year } }).then((res) => res.data);
      setSummary(r);
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year]);

  const connect = async () => {
    setConnecting(true);
    try {
      const { data } = await api.post('/driver/stripe/onboarding', {});
      window.location.href = data.onboarding_url;
    } catch (e) { toast.error(e?.response?.data?.detail || 'Stripe Connect is unavailable'); }
    finally { setConnecting(false); }
  };

  const downloadCsv = async () => {
    const token = localStorage.getItem('swiftdash_token');
    try {
      const res = await fetch(`${api.defaults.baseURL}/driver/tax/summary.csv?year=${year}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('failed');
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `driver_earnings_${year}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error('Download failed'); }
  };

  const needsStripe = !status?.connected || !status?.onboarded;

  return (
    <div className="mt-6 space-y-6" data-testid="driver-marketplace-panel">
      {needsStripe && (
        <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 flex items-start gap-3" data-testid="driver-stripe-reminder">
          <ShieldAlert className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="font-semibold text-amber-900">Set up your payouts</div>
            <p className="mt-1 text-sm text-amber-800">
              Connect your Stripe account to get paid for your deliveries.
              You&apos;re responsible for filing your annual tax return with the government — the CSV below tracks every delivery paid to you.
            </p>
            <div className="mt-3">
              <Button onClick={connect} disabled={connecting} className="bg-[#635BFF] hover:bg-[#4f47e6] text-white" data-testid="driver-connect-stripe-btn">
                {connecting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ExternalLink className="h-4 w-4 mr-1.5" />}
                Connect with Stripe
              </Button>
            </div>
          </div>
        </div>
      )}

      <section className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="driver-tax-tile">
        <div className="flex items-center justify-between gap-2 mb-3">
          <h2 className="font-semibold text-slate-900">Annual earnings ({summary?.year || year})</h2>
          <div className="flex items-center gap-2">
            <select value={year} onChange={(e) => setYear(Number(e.target.value))}
              className="h-8 rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid="driver-tax-year-select">
              {[CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2].map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            <Button onClick={downloadCsv} variant="outline" size="sm" data-testid="driver-download-csv-btn">
              <Download className="h-4 w-4 mr-1.5" /> CSV
            </Button>
          </div>
        </div>
        {loading ? (
          <Loader2 className="h-5 w-5 animate-spin text-emerald-600" />
        ) : summary ? (
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-3">
              <div className="text-xs text-slate-500">Deliveries</div>
              <div className="mt-1 font-semibold text-slate-900">{summary.orders_count}</div>
            </div>
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-3">
              <div className="text-xs text-slate-500">Gross earnings</div>
              <div className="mt-1 font-semibold text-emerald-700">{format(summary.gross_earnings || 0)}</div>
            </div>
          </div>
        ) : (
          <div className="text-sm text-slate-500">No deliveries yet in {year}.</div>
        )}
        <p className="mt-3 text-[11px] text-slate-500">
          You&apos;re an independent contractor. This CSV lists every delivery paid to you in {year} — use it to file your annual tax return.
        </p>
      </section>
    </div>
  );
};

export default DriverMarketplacePanel;
